import { useTranslation } from "react-i18next";
import { Calendar, MapPin, Car, Camera, Image as ImageIcon, Upload, AlertTriangle, Star, CheckCircle2, Clock, XCircle } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ClaimThread } from "@/components/ClaimThread";

const STATUS = {
  confirmed: { label: "Confirmée", cls: "bg-emeraude/15 text-emeraude-dark", icon: CheckCircle2 },
  pending_payment: { label: "En attente", cls: "bg-yellow-100 text-yellow-700", icon: Clock },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-600", icon: XCircle },
  ongoing: { label: "En cours", cls: "bg-canard/15 text-canard-dark", icon: Car },
  completed: { label: "Terminée", cls: "bg-gray-100 text-gray-600", icon: CheckCircle2 },
};

export function BookingCard({ b, canReview, onReview, onCancel, onDispute, onUploadCondition, onClaim, onClaimMessage }) {
  const { t } = useTranslation();
  const st = STATUS[b.status] || STATUS.pending_payment;
  return (
    <div data-testid={`booking-${b.id}`} className="bg-white rounded-2xl shadow-card border border-border p-5 flex flex-col sm:flex-row gap-4">
      <img src={b.vehicle_photo} alt="" className="w-full sm:w-40 h-28 object-cover rounded-xl" />
      <div className="flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-heading font-bold text-lg text-ink">{b.vehicle_name}</h3>
            <p className="text-xs text-muted2 font-mono">{b.ref}</p>
          </div>
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${st.cls}`}><st.icon className="w-3 h-3" /> {st.label}</span>
        </div>
        <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted2 font-body">
          <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-canard" /> {b.start_date} → {b.end_date}</span>
          <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-canard" /> {b.pickup_location}</span>
        </div>
        {["confirmed", "ongoing"].includes(b.status) && (
          <div data-testid={`photo-tip-${b.id}`} className="mt-3 flex gap-2 bg-pastel/30 rounded-xl p-3">
            <Camera className="w-5 h-5 text-canard shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-canard-dark">{t("dash.photoTipTitle")}</p>
              <p className="text-xs text-muted2 font-body">{t("dash.photoTipText")}</p>
            </div>
          </div>
        )}
        {b.deposit_status === "captured" && b.deposit_damage_photos?.length > 0 && (
          <div data-testid={`damage-gallery-${b.id}`} className="mt-3 bg-red-50 border border-red-100 rounded-xl p-3">
            <p className="text-xs font-bold text-red-600 flex items-center gap-1"><ImageIcon className="w-4 h-4" /> {t("dash.damageGalleryTitle")}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {b.deposit_damage_photos.map((p) => (
                <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="block w-24 h-20 rounded-lg overflow-hidden border border-red-200">
                  <img src={fileUrl(p)} alt="dommage" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}
        {b.deposit_status === "captured" && (
          <div className="mt-2">
            {b.deposit_dispute ? (
              <span data-testid={`dispute-status-${b.id}`} className={`text-xs font-bold px-2 py-0.5 rounded-full ${b.deposit_dispute.status === "resolved" ? "bg-emeraude/15 text-emeraude-dark" : "bg-yellow-100 text-yellow-700"}`}>
                {b.deposit_dispute.status === "resolved" ? t("dash.disputeResolved") : t("dash.disputeOpen")}
              </span>
            ) : (
              <Button size="sm" variant="outline" data-testid={`dispute-button-${b.id}`} onClick={() => onDispute(b)} className="rounded-full text-destructive border-destructive/30 h-8">
                <AlertTriangle className="w-3.5 h-3.5 mr-1" /> {t("dash.contest")}
              </Button>
            )}
          </div>
        )}
        {["confirmed", "ongoing", "completed"].includes(b.status) && (
          <div data-testid={`condition-photos-${b.id}`} className="mt-3 bg-sand rounded-xl p-3">
            <p className="text-xs font-bold text-canard-dark flex items-center gap-1"><Camera className="w-4 h-4" /> {t("dash.conditionPhotos")}</p>
            {(b.condition_photos || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {b.condition_photos.map((p) => (
                  <a key={p.url} href={fileUrl(p.url)} target="_blank" rel="noreferrer" className="relative block w-20 h-16 rounded-lg overflow-hidden border border-border">
                    <img src={fileUrl(p.url)} alt="" className="w-full h-full object-cover" />
                    <span className={`absolute bottom-0 inset-x-0 text-[9px] font-bold text-white text-center ${p.by === "loueur" ? "bg-canard" : "bg-emeraude"}`}>{p.phase === "before" ? "↑" : "↓"}{p.by === "loueur" ? "L" : "C"}</span>
                  </a>
                ))}
              </div>
            )}
            {["confirmed", "ongoing"].includes(b.status) && (
              <div className="flex gap-2 mt-2">
                <label data-testid={`upload-before-${b.id}`} className="text-xs font-semibold text-canard cursor-pointer flex items-center gap-1 bg-white rounded-full px-3 py-1 border border-border hover:border-canard"><Upload className="w-3.5 h-3.5" /> {t("dash.addPhotosBefore")}<input type="file" accept="image/*" className="hidden" onChange={(e) => onUploadCondition(b, "before", e)} /></label>
                <label data-testid={`upload-after-${b.id}`} className="text-xs font-semibold text-canard cursor-pointer flex items-center gap-1 bg-white rounded-full px-3 py-1 border border-border hover:border-canard"><Upload className="w-3.5 h-3.5" /> {t("dash.addPhotosAfter")}<input type="file" accept="image/*" className="hidden" onChange={(e) => onUploadCondition(b, "after", e)} /></label>
              </div>
            )}
          </div>
        )}
        {["confirmed", "ongoing"].includes(b.status) && (
          <div className="mt-3" data-testid={`claim-section-${b.id}`}>
            {b.client_claim ? (
              <div className="bg-red-50 border border-red-100 rounded-xl p-2.5">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                  <p className="text-xs text-red-700 font-body flex-1"><span className="font-bold">{t(`claims.r_${b.client_claim.reason}`)}</span>{b.client_claim.details ? ` · ${b.client_claim.details}` : ""}</p>
                  <span data-testid={`claim-status-${b.id}`} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${b.client_claim.status === "resolved" ? "bg-emeraude/15 text-emeraude-dark" : b.client_claim.status === "rejected" ? "bg-gray-200 text-gray-600" : "bg-yellow-100 text-yellow-700"}`}>{t(b.client_claim.status === "resolved" ? "claims.statusResolved" : b.client_claim.status === "rejected" ? "claims.statusRejected" : "claims.statusOpen")}</span>
                </div>
                <ClaimThread messages={b.client_claim.messages} onSend={(txt) => onClaimMessage(b, "client", txt)} testid={`claim-thread-${b.id}`} />
              </div>
            ) : (
              <Button size="sm" variant="outline" data-testid={`claim-button-${b.id}`} onClick={() => onClaim(b)} className="rounded-full text-destructive border-destructive/30 h-8">
                <AlertTriangle className="w-3.5 h-3.5 mr-1" /> {t("claims.reportProblem")}
              </Button>
            )}
          </div>
        )}
        {b.loueur_claim && (
          <div data-testid={`loueur-claim-note-${b.id}`} className="mt-2 bg-yellow-50 border border-yellow-200 rounded-xl p-2.5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600 shrink-0" />
              <p className="text-xs text-yellow-800 font-body flex-1"><span className="font-bold">{t("claims.loueurReport")}:</span> {b.loueur_claim.type === "no_show" ? t("claims.typeNoShow") : t(`claims.d_${b.loueur_claim.reason}`)}{b.loueur_claim.details ? ` · ${b.loueur_claim.details}` : ""}</p>
            </div>
            <ClaimThread messages={b.loueur_claim.messages} onSend={(txt) => onClaimMessage(b, "loueur", txt)} testid={`loueur-claim-thread-${b.id}`} />
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-border">
          <span className="font-heading font-bold text-canard-dark">{b.amount} € <span className="text-xs text-muted2 font-body">· {t("vehicle.deposit")} {b.deposit} €</span></span>
          <div className="flex gap-2">
            {canReview(b) && (
              <Button variant="outline" size="sm" data-testid={`review-booking-${b.id}`} onClick={() => onReview(b)} className="rounded-full text-emeraude-dark border-emeraude/30">
                <Star className="w-3.5 h-3.5 mr-1" /> {t("dash.writeReview")}
              </Button>
            )}
            {b.reviewed && <span className="text-xs text-emeraude-dark font-semibold flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-emeraude text-emeraude" /> {t("dash.verifiedBadge")}</span>}
            {["confirmed", "pending_payment"].includes(b.status) && (
              <Button variant="outline" size="sm" data-testid={`cancel-booking-${b.id}`} onClick={() => onCancel(b.id)} className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/5">
                {t("common.cancel")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
